import React, { useMemo } from 'react';
import { MenuItem, Select } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { BaseTypography, BaseTextField, BaseDivider } from '../index';
import { SearchTextField } from '../BaseTextField/SearchTextField';
import { BasePagination } from '../BasePagination/BasePagination';
import { IPagination } from '../../../api/base/baseApiTypes';
import { horizontalPadding } from './BaseTableCard';
import { debounce } from '../../../utils/helpers';

const useStyles = makeStyles((theme) => ({
  tableContainer: {},
  tableContainer__filters: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      display: 'grid',
      gridGap: '10px',
      width: '100%',
    },
  },
  tableContainer__field: {
    display: 'flex',
    alignItems: 'center',
  },
  tableContainer__searchField: {
    marginLeft: '10px',
    width: '100%',
  },
  tableContainer__tableWrapper: {
    marginTop: '20px',
    overflowX: 'auto',
    overflowY: 'hidden',
    width: `calc(100% + ${horizontalPadding} * 2)`,
    marginLeft: `-${horizontalPadding}`,
    padding: `${horizontalPadding} ${horizontalPadding} 0`,
  },
  paginationContainer: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    alignItems: 'center',
    justifyContent: 'space-between',
    gridGap: '10px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '100%',
    },
  },
}));

type PBaseTableContainer = {
  onPerPageChange: (perPage: number) => void;
  onPageChange: (page: number) => void;
  perPage: number;
  pagination?: IPagination;
  onSearch: (value: string) => void;
  currentPage: number;
};

export const BaseTableContainer: React.FC<PBaseTableContainer> = (props) => {
  const classes = useStyles();
  const {
    children,
    onPerPageChange,
    onPageChange,
    pagination,
    perPage,
    onSearch,
    currentPage,
  } = props;
  // Trust me, in this case you have to use useMemo instead of useCallback.
  // useMemo returns result of execution of function.
  const debouncedSearchHandler = useMemo(
    () => debounce(onSearch, 500),
    [onSearch],
  );

  const startShowingNumber =
    (pagination && pagination?.perPage * (pagination?.currentPage - 1) + 1) ||
    0;
  const lastPossibleNumberOnPage =
    (pagination && pagination.perPage * pagination.currentPage) || 0;

  const endShowingNumber =
    lastPossibleNumberOnPage > (pagination?.total || 0)
      ? pagination?.total || 0
      : lastPossibleNumberOnPage;

  return (
    <div className={classes.tableContainer}>
      <div className={classes.tableContainer__filters}>
        <div className={classes.tableContainer__field}>
          <BaseTypography variant="tag">Entries</BaseTypography>
          <Select
            value={perPage}
            className={classes.tableContainer__searchField}
            input={
              <BaseTextField size="small" style={{ lineHeight: '14px' }} />
            }
            style={{ height: '35px' }}
            onChange={(e) => onPerPageChange(Number(e.target.value))}
          >
            <MenuItem value="5">5</MenuItem>
            <MenuItem value="10">10</MenuItem>
            <MenuItem value="15">15</MenuItem>
          </Select>
        </div>

        <div className={classes.tableContainer__field}>
          <BaseTypography variant="tag">Search</BaseTypography>
          <SearchTextField
            size="small"
            icoBtn
            className={classes.tableContainer__searchField}
            onChange={(e: any) => {
              debouncedSearchHandler(e.target.value);
            }}
          />
        </div>
      </div>

      <div className={classes.tableContainer__tableWrapper}>{children}</div>

      <BaseDivider mTop="5px" />

      <div className={classes.paginationContainer}>
        <BaseTypography variant="smallText">
          Showing {pagination?.total ? startShowingNumber : 0} to{' '}
          {endShowingNumber} of&nbsp;
          {pagination?.total} entries
        </BaseTypography>
        <BasePagination
          count={pagination?.lastPage || 1}
          page={currentPage}
          variant="outlined"
          shape="rounded"
          onChange={(_, newPage) => {
            onPageChange(newPage);
          }}
        />
      </div>
    </div>
  );
};
